import { useNavigation } from '@react-navigation/native';
import { Button, Meta, theme } from 'aimo-ui';
import AccountContext from 'context/AccountContext';
import useDynamicSheetSize from 'hooks/useDynamicSheetSize';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppConfigStore } from 'stores/appConfigStore';
import { usePermitStore } from 'stores/permitStore';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';
import { CityParkingNavProps, RootNavProps } from 'types/navigation';
import Logger from 'utils/Logger';
import { isPmcAvailable } from 'utils/permitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

const ItemUnavailableModalContent = ({
  contentType,
}: {
  contentType: 'duplicatePermitWarning' | 'permitActivationNeeded';
}) => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<RootNavProps>();
  const { navigate: navigateToCity, goBack: goBackCity } =
    useNavigation<CityParkingNavProps>();
  const { handleLayout } = useDynamicSheetSize(20);

  const selectedNavigateType = useAppConfigStore((state) => state.navigateType);
  const {
    customer: { permits },
  } = useContext(AccountContext);

  const {
    pmcIsLockedByMemberGroup,
    resetPmcIsLockedByMemberGroup,
    selectedPermit,
    selectedPmc,
  } = usePermitStore();

  const pmc = selectedPmc;
  const { pmcId, parkZone, visibility } = pmc ?? {};
  const parkingZoneUid = parkZone?.uid ?? '';
  const permitId = selectedPermit;

  const closeModal = (): void => {
    resetPmcIsLockedByMemberGroup();
    if (selectedNavigateType === 'CityParkingPmcItem') {
      return goBackCity();
    }
    useUiStore.getState().setSheetModal('');
  };

  const handleNavigateToPermit = (): void => {
    if (!permitId) {
      Logger.error('invalid operation: goToPermit without permitId');
      closeModal();
      return;
    }

    if (
      permits?.find((p) => p.id === permitId && p.permitType === 'WAITING_LIST')
    ) {
      navigate('longTermParking', {
        screen: 'waitingListPermitDetails',
        params: { id: permitId },
      });
    } else if (permits?.find((p) => p.orderLineIdWithVersion === permitId)) {
      navigate('salesOrderPermitEdit', { orderLineIdWithVersion: permitId });
    } else {
      navigate('permit', { permitId });
    }
    closeModal();
  };

  const handleNavigateToPermitForActivation = (): void => {
    if (permitId) {
      closeModal();
      navigate('salesOrderPermitEdit', { orderLineIdWithVersion: permitId });
    }
  };

  const handleBuyPermit = (): void => {
    if (!pmcId) {
      return;
    }

    const { pmcIsLockedForTheCustomer, pmcIsInsideALockedMemberGroup } =
      pmcIsLockedByMemberGroup[pmcId] ?? {};
    closeModal();

    if (pmcIsLockedForTheCustomer && pmcIsInsideALockedMemberGroup) {
      useUiStore.getState().setSheetModal('unlockMemberGroupPmc');
      return;
    }

    if (!pmcIsInsideALockedMemberGroup && visibility === 'LOCKED') {
      useUiStore.getState().setSheetModal('unlockPmc');
      return;
    }

    if (selectedNavigateType === 'CityParkingPmcItem') {
      return navigateToCity('purchase');
    }

    if (isPmcAvailable(pmc).showWaitingList) {
      return navigate('longTermParking', {
        screen: 'waitingListInfo',
        params: { parkingZoneUid, pmcId },
      });
    }

    navigate('longTermParking', {
      screen: 'buyScreen',
      params: { parkingZoneUid, pmcId },
    });
  };

  const renderButtons = () => {
    const notCityParking = selectedNavigateType !== 'CityParkingPmcItem';
    const texts = {
      permitActivationNeeded: {
        meta: t('services.permitActivationNeeded'),
        buttons: [
          {
            text: t('button.activate'),
            onPress: handleNavigateToPermitForActivation,
          },
        ],
      },
      duplicatePermitWarning: {
        meta: t(
          notCityParking ? 'services.duplicatePermit' : 'services.ongoingPermit'
        ),
        buttons: [
          ...(notCityParking
            ? [
                {
                  text: t('button.buyPermit'),
                  onPress: handleBuyPermit,
                },
              ]
            : []),
          {
            text: t('button.goToPermit'),
            onPress: handleNavigateToPermit,
          },
        ],
      },
    };

    return (
      texts[contentType as keyof typeof texts] || { meta: '', buttons: [] }
    );
  };

  const { meta, buttons } = renderButtons();

  return (
    <StyledWrapper mb={0} onLayout={(e) => handleLayout(e.nativeEvent.layout)}>
      <StyledWrapper mb={60} center testID="item-unavailable-description">
        <Meta center color={theme.colors.gray300}>
          {meta}
        </Meta>
      </StyledWrapper>
      <>
        {buttons.map((button, index) => (
          <ColumnWithMargins mb={10} key={button.text}>
            <Button
              {...testIdentifiers('item-unavailable-go-to-permit')}
              text={button.text}
              size="l"
              buttonType={index === 0 ? 'secondary' : 'tertiary'}
              onPress={button.onPress}
            />
          </ColumnWithMargins>
        ))}
        <Button
          testID="item-unavailable-cancel"
          text={t('button.cancel')}
          size="l"
          buttonType="tertiary"
          onPress={closeModal}
        />
      </>
    </StyledWrapper>
  );
};

export default ItemUnavailableModalContent;
